/**
 * Service OpenAI pour la génération de documents.
 * Tous les appels passent par le backend (OPENAI_API_KEY côté serveur uniquement).
 */

import { chatCompletions } from './openaiBackend';

interface GenerateDocumentsRequest {
  responses: Record<string, string | string[]>;
}

interface GeneratedDocuments {
  cahierDeCharge: string;
  roadmap: string;
  planDirecteur?: string;
  ods?: string;
}

export interface GeneratedManagedDocument {
  content: string;
  jobId?: string;
}

export async function generateDocumentsWithAI(
  request: GenerateDocumentsRequest
): Promise<GeneratedDocuments> {
  const { responses } = request;

  // Préparer le contexte pour l'IA
  const context = buildContextFromResponses(responses);

  try {
    // Générer les Spécifications d'exigences d'affaires et TI
    const cahierDeCharge = await generateCahierDeCharge(context, responses);
    
    // Générer le Roadmap
    const roadmap = await generateRoadmap(context, responses);
    
    // Générer Plan Directeur si applicable
    const planDirecteur = shouldGeneratePlanDirecteur(responses)
      ? await generatePlanDirecteur(context, responses)
      : undefined;
    
    // Générer ODS si applicable
    const ods = shouldGenerateODS(responses)
      ? await generateODS(context, responses)
      : undefined;

    return {
      cahierDeCharge,
      roadmap,
      planDirecteur,
      ods,
    };
  } catch (error) {
    console.error('Erreur lors de la génération des documents:', error);
    throw new Error('Impossible de générer les documents. Veuillez réessayer.');
  }
}

interface GenerateManagedDocumentRequest {
  documentTypeCode: string;
  documentTypeTitle: string;
  qualityLevel: number;
  qualityLabel: string;
  frameworks: string[];
  context?: string;
  /** ID du template de prise de besoin (ex: pdti-pme-v2) pour adapter les piliers (7 vs 5) et les viz */
  templateId?: string;
  sourceSchema?: number;
  sourceVersion?: string;
  besoinId?: number;
}

const JOB_POLL_INTERVAL_MS = 2500;
const JOB_POLL_TIMEOUT_MS = 15 * 60 * 1000; // 15 min

export async function generateManagedDocumentWithAI(
  params: GenerateManagedDocumentRequest,
  onProgress?: (progress: { part: number; total: number; progress: number; message: string }) => void
): Promise<GeneratedManagedDocument> {
  // Mode job + polling : évite les timeouts proxy en prod (connexion longue SSE coupée ~3 min)
  const res = await fetch('/api/prisme/documents/generate-managed', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Use-Job': 'true',
    },
    credentials: 'include',
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error((errBody as { error?: string }).error || `Erreur HTTP: ${res.status}`);
  }

  if (res.status !== 202) {
    // Backend ancien sans mode job : fallback SSE (dev uniquement, peut timeout en prod)
    return streamSSEGeneration(params, onProgress);
  }

  const { jobId } = (await res.json()) as { jobId: string };
  if (!jobId) throw new Error('Réponse serveur invalide (jobId manquant)');

  const startedAt = Date.now();
  const poll = (): Promise<GeneratedManagedDocument> =>
    fetch(`/api/prisme/documents/generate-managed/status/${jobId}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Statut job: ${r.status}`);
        return r.json() as Promise<{
          status: string;
          progress?: { part: number; total: number; progress: number; message: string };
          content?: string | null;
          error?: string | null;
        }>;
      })
      .then((job) => {
        if (job.status === 'complete') {
          if (job.content != null) return { content: job.content, jobId };
          throw new Error('Job terminé sans contenu');
        }
        if (job.status === 'error') {
          throw new Error(job.error || 'Erreur lors de la génération');
        }
        if (job.progress && onProgress) onProgress(job.progress);
        if (Date.now() - startedAt > JOB_POLL_TIMEOUT_MS) {
          throw new Error('Délai dépassé. La génération prend trop de temps.');
        }
        return new Promise<GeneratedManagedDocument>((resolve, reject) => {
          setTimeout(() => poll().then(resolve).catch(reject), JOB_POLL_INTERVAL_MS);
        });
      });

  return poll();
}

async function streamSSEGeneration(
  params: GenerateManagedDocumentRequest,
  onProgress?: (progress: { part: number; total: number; progress: number; message: string }) => void
): Promise<GeneratedManagedDocument> {
  return new Promise((resolve, reject) => {
    fetch('/api/prisme/documents/generate-managed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(params),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error((error as { error?: string }).error || `Erreur HTTP: ${response.status}`);
        }
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) throw new Error('Impossible de lire le stream de réponse');
        let buffer = '';
        const readChunk = (): Promise<void> =>
          reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                reject(new Error('Stream terminé sans résultat complet'));
                return;
              }
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';
              for (const line of lines) {
                if (line.startsWith(':')) continue;
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6)) as {
                      type?: string;
                      content?: string;
                      error?: string;
                      part?: number;
                      total?: number;
                      progress?: number;
                      message?: string;
                    };
                    if (data.type === 'complete') {
                      resolve({ content: data.content ?? '' });
                      return;
                    }
                    if (data.type === 'error') {
                      reject(new Error(data.error));
                      return;
                    }
                    if (data.part !== undefined && onProgress) {
                      onProgress({
                        part: data.part ?? 0,
                        total: data.total ?? 1,
                        progress: data.progress ?? 0,
                        message: data.message ?? '',
                      });
                    }
                  } catch (e) {
                    console.error('Erreur parsing SSE:', e);
                  }
                }
              }
              return readChunk();
            })
            .catch(reject);
        return readChunk();
      })
      .catch((err) => {
        if (err instanceof TypeError && err.message === 'network error') {
          reject(new Error('Connexion réseau interrompue. Veuillez réessayer.'));
        } else if (err.name === 'AbortError') {
          reject(new Error('La requête a été annulée.'));
        } else {
          reject(err);
        }
      });
  });
}

function buildContextFromResponses(responses: Record<string, string | string[]>): string {
  const formatValue = (value: string | string[]): string => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  return Object.entries(responses)
    .map(([key, value]) => `${key}: ${formatValue(value)}`)
    .join('\n');
}

async function generateCahierDeCharge(
  context: string,
  responses: Record<string, string | string[]>
): Promise<string> {
  const currentSystems = Array.isArray(responses.current_systems) ? responses.current_systems.join(', ') : '';

  const prompt = `Tu es un expert consultant TI chez Bergeron Zaccheo (BZ), une firme de conseil en transformation numérique reconnue pour son excellence.

Génère des SPÉCIFICATIONS D'EXIGENCES D'AFFAIRES ET TI professionnelles et détaillées pour le client suivant:

INFORMATIONS CLIENT:
${context}

Les spécifications d'exigences d'affaires et TI doivent inclure les sections suivantes avec beaucoup de détails:

1. CONTEXTE ET ENJEUX D'AFFAIRES
   - Présentation de l'entreprise et son secteur
   - Défi principal et problématiques identifiées
   - Enjeux stratégiques et opportunités

2. OBJECTIFS DU PROJET
   - Objectifs d'affaires mesurables
   - Objectifs TI spécifiques
   - Critères de succès et KPIs

3. PÉRIMÈTRE ET LIVRABLES
   - Périmètre fonctionnel détaillé
   - Périmètre technique
   - Livrables attendus
   - Exclusions explicites

4. EXIGENCES FONCTIONNELLES
   - Besoins métier détaillés par processus
   - Flux de travail et intégrations
   - Interfaces utilisateur requises

5. EXIGENCES TECHNIQUES
   - Architecture technique recommandée
   - Infrastructure et plateformes
   - Intégrations avec systèmes existants: ${currentSystems}
   - Sécurité et conformité
   - Performance et disponibilité

6. EXIGENCES NON-FONCTIONNELLES
   - Sécurité et confidentialité
   - Performance et scalabilité
   - Disponibilité et continuité
   - Ergonomie et expérience utilisateur

7. CONTRAINTES ET RISQUES
   - Contraintes techniques et organisationnelles
   - Risques identifiés et mitigation
   - Dépendances critiques

8. ORGANISATION ET GOUVERNANCE
   - Parties prenantes et rôles RACI
   - Comité de pilotage
   - Processus de décision et escalade

9. PLANNING ET JALONS
   - Phases du projet
   - Jalons majeurs
   - Chronologie estimée

10. BUDGET ET RESSOURCES
    - Estimation budgétaire par phase
    - Ressources requises (équipe, infrastructure)
    - Modèle de facturation

Sois très spécifique, professionnel et orienté solutions. Utilise un langage clair et structuré. Minimum 2000 mots.`;

  return await callOpenAI(prompt);
}

async function generateRoadmap(
  context: string,
  responses: Record<string, string | string[]>
): Promise<string> {
  const priorities = Array.isArray(responses.priorities) ? responses.priorities : [];

  const prompt = `Tu es un expert consultant TI chez Bergeron Zaccheo (BZ), spécialisé en planification stratégique.

Génère un ROADMAP ANNUEL détaillé et actionnable pour le client suivant:

INFORMATIONS CLIENT:
${context}

Le roadmap doit inclure:

1. VISION ET OBJECTIFS
   - Vision stratégique à 12 mois
   - Objectifs prioritaires: ${priorities.join(', ')}
   - Bénéfices attendus par trimestre

2. ROADMAP TRIMESTRIEL DÉTAILLÉ

   Q1 (Mois 1-3):
   - Projets et initiatives
   - Livrables clés
   - Ressources nécessaires
   - Risques et dépendances
   - Budget estimé
   - KPIs de succès

   Q2 (Mois 4-6):
   - Projets et initiatives
   - Livrables clés
   - Ressources nécessaires
   - Risques et dépendances
   - Budget estimé
   - KPIs de succès

   Q3 (Mois 7-9):
   - Projets et initiatives
   - Livrables clés
   - Ressources nécessaires
   - Risques et dépendances
   - Budget estimé
   - KPIs de succès

   Q4 (Mois 10-12):
   - Projets et initiatives
   - Livrables clés
   - Ressources nécessaires
   - Risques et dépendances
   - Budget estimé
   - KPIs de succès

3. JALONS CRITIQUES
   - Dates clés et échéances
   - Points de décision Go/No-Go
   - Revues trimestrielles

4. GESTION DES DÉPENDANCES
   - Dépendances inter-projets
   - Ressources partagées
   - Risques transversaux

5. TABLEAU DE BORD
   - KPIs de suivi par trimestre
   - Indicateurs de santé projet
   - Métriques de valeur livrée

Sois très spécifique sur les actions concrètes, les timelines et les ressources. Utilise un format professionnel et visuel (tableaux, diagrammes en texte). Minimum 1500 mots.`;

  return await callOpenAI(prompt);
}

async function generatePlanDirecteur(
  context: string,
  responses: Record<string, string | string[]>
): Promise<string> {
  const businessGoals = Array.isArray(responses.business_goals) ? responses.business_goals : [];

  const prompt = `Tu es un expert consultant TI chez Bergeron Zaccheo (BZ), spécialisé en stratégie TI à long terme.

Génère un PLAN DIRECTEUR TI (3-5 ans) stratégique et visionnaire pour le client suivant:

INFORMATIONS CLIENT:
${context}

Le Plan Directeur doit inclure:

1. RÉSUMÉ EXÉCUTIF
   - Vision TI à 3-5 ans
   - Alignement stratégique avec les objectifs d'affaires: ${businessGoals.join(', ')}
   - Investissements majeurs et ROI attendu
   - Facteurs clés de succès

2. ÉTAT DES LIEUX (AS-IS)
   - Maturité TI actuelle
   - Forces et faiblesses de l'infrastructure
   - Dette technique
   - Opportunités d'amélioration

3. VISION CIBLE (TO-BE)
   - Architecture cible
   - Capacités TI futures
   - Innovation et différenciation
   - Excellence opérationnelle

4. AXES STRATÉGIQUES

   Axe 1: Infrastructure et Cloud
   - Vision et objectifs
   - Initiatives majeures
   - Timeline 3-5 ans
   - Investissements requis

   Axe 2: Sécurité et Conformité
   - Vision et objectifs
   - Initiatives majeures
   - Timeline 3-5 ans
   - Investissements requis

   Axe 3: Applications et Données
   - Vision et objectifs
   - Initiatives majeures
   - Timeline 3-5 ans
   - Investissements requis

   Axe 4: Innovation et IA
   - Vision et objectifs
   - Initiatives majeures
   - Timeline 3-5 ans
   - Investissements requis

   Axe 5: Équipe et Compétences
   - Vision et objectifs
   - Initiatives majeures
   - Timeline 3-5 ans
   - Investissements requis

5. ROADMAP PLURIANNUEL
   - Année 1: Fondations et stabilisation
   - Année 2: Transformation et modernisation
   - Année 3: Innovation et optimisation
   - Année 4-5: Excellence et leadership

6. GOUVERNANCE ET ORGANISATION
   - Modèle de gouvernance TI
   - Structure organisationnelle cible
   - Processus et méthodologies
   - Culture et changement

7. BUDGET ET FINANCEMENT
   - Budget pluriannuel par axe
   - TCO et optimisation des coûts
   - Modèles de financement
   - ROI et business case

8. GESTION DES RISQUES
   - Risques stratégiques
   - Plans de mitigation
   - Scénarios et contingences

9. MÉTRIQUES DE SUCCÈS
   - KPIs stratégiques
   - Tableau de bord exécutif
   - Processus de suivi et revue

Sois ambitieux, visionnaire mais pragmatique. Démontre une compréhension profonde des enjeux d'affaires. Minimum 2500 mots.`;

  return await callOpenAI(prompt);
}

async function generateODS(
  context: string,
  responses: Record<string, string | string[]>
): Promise<string> {
  const supportModel = responses.support_model as string;
  const trainingNeeds = responses.training_needs as string;

  const prompt = `Tu es un expert consultant TI chez Bergeron Zaccheo (BZ), spécialisé en design d'offres de service.

Génère une OFFRE DE SERVICE (ODS) complète et professionnelle pour le client suivant:

INFORMATIONS CLIENT:
${context}

L'Offre de Service doit inclure:

1. PRÉSENTATION DE L'OFFRE
   - Vision et engagement BZ
   - Valeur ajoutée pour le client
   - Principes de service

2. CATALOGUE DE SERVICES

   A. Services de Support et Maintenance
   - Description détaillée
   - Périmètre et exclusions
   - Niveaux de service (SLA)
   - Heures de disponibilité
   - Temps de réponse par priorité
   - Processus d'escalade

   B. Services de Gestion et Monitoring
   - Surveillance proactive
   - Gestion des incidents
   - Gestion des changements
   - Rapports et tableaux de bord

   C. Services de Conseil et Optimisation
   - Audits et évaluations
   - Recommandations d'amélioration
   - Veille technologique
   - Planification stratégique

   D. Services de Formation
   - Programme de formation: ${trainingNeeds}
   - Formation utilisateurs
   - Formation administrateurs
   - Documentation et guides

   E. Services de Sécurité
   - Monitoring sécurité
   - Gestion des vulnérabilités
   - Conformité et audits
   - Plan de continuité

3. NIVEAUX DE SERVICE (SLA)

   Disponibilité:
   - Objectifs de disponibilité par service
   - Fenêtres de maintenance
   - Compensation en cas de non-respect

   Performance:
   - Temps de réponse garantis
   - Métriques de performance
   - Seuils d'alerte

   Support:
   - Priorité P1 (Critique): X heures
   - Priorité P2 (Majeur): Y heures
   - Priorité P3 (Mineur): Z heures
   - Priorité P4 (Question): W heures

4. MODÈLE D'ENGAGEMENT
   - Type de contrat recommandé: ${supportModel}
   - Heures incluses mensuellement
   - Heures additionnelles
   - Services on-demand
   - Flexibilité et ajustements

5. ORGANISATION ET GOUVERNANCE
   - Équipe BZ dédiée (RACI)
   - Point de contact unique
   - Comités de gouvernance
   - Processus de communication
   - Revues mensuelles/trimestrielles

6. OUTILS ET TECHNOLOGIES
   - Plateformes de ticketing
   - Outils de monitoring
   - Portail client
   - Rapports automatisés

7. TARIFICATION

   Forfait de Base:
   - Services inclus
   - Heures mensuelles
   - Prix mensuel

   Services Additionnels:
   - Tarif horaire par expertise
   - Services à la demande
   - Projets spéciaux

   Modèles de Facturation:
   - Mensuel récurrent
   - À l'usage
   - Forfait annuel (avec rabais)

8. INDICATEURS DE PERFORMANCE
   - KPIs de service
   - Satisfaction client (CSAT, NPS)
   - Temps de résolution moyen
   - Taux de résolution au premier contact
   - Disponibilité réelle

9. PROCESSUS ET MÉTHODOLOGIES
   - Gestion des incidents (ITIL)
   - Gestion des changements
   - Gestion des problèmes
   - Amélioration continue

10. ENGAGEMENT ET GARANTIES
    - Garanties de service
    - Pénalités en cas de non-respect
    - Processus de réclamation
    - Conditions de résiliation

Sois très détaillé, professionnel et orienté client. Démontre la valeur de BZ. Minimum 2000 mots.`;

  return await callOpenAI(prompt);
}

async function callOpenAI(prompt: string): Promise<string> {
  return chatCompletions({
    model: 'gpt-4o',
    messages: [
      {
        role: 'system',
        content: 'Tu es un consultant TI expert senior chez Bergeron Zaccheo (BZ), une firme de conseil réputée. Tu génères des documents professionnels, détaillés et orientés solutions. Tu utilises un français impeccable et un ton professionnel mais accessible.',
      },
      { role: 'user', content: prompt },
    ],
    temperature: 0.7,
    max_tokens: 4000,
  });
}

function shouldGeneratePlanDirecteur(responses: Record<string, string | string[]>): boolean {
  const timeline = responses.timeline as string;
  const employees = responses.employees as string;
  
  // Générer le Plan Directeur pour:
  // - Entreprises 50+ employés
  // - Timeline moyen/long terme (6 mois+)
  // - Ou toute entreprise qui veut une vision stratégique
  return (
    timeline?.includes('Stratégique') ||
    timeline?.includes('Long terme') ||
    timeline?.includes('Moyen terme') ||
    employees?.includes('250 employés et plus') ||
    employees?.includes('100-250 employés') ||
    employees?.includes('50-100 employés') ||
    true // Pour l'instant, toujours générer pour avoir 3 onglets
  );
}

function shouldGenerateODS(responses: Record<string, string | string[]>): boolean {
  const supportModel = responses.support_model as string;
  return supportModel !== 'Support interne - notre équipe gère';
}

